import { recordStatusSchema, roleGroupTypes } from '../types';
import useQueryTeamRoles from '../../../hooks/useQueryTeamRoles';
import { onTeamRoleSearch, generateAntdFormItemId } from './util';
import { useState } from 'react';
import type { StoredTeamRole } from '../../../hooks/types';
import type { GroupWithOptions } from '../../AccessibleComponents/GroupSelect/types';
import GroupSelect from '../../AccessibleComponents/GroupSelect';
import { teamRolesByStatus } from '../TeamSettings/util';
import type { FormListFieldData } from 'antd';
import { onApolloError } from '../../../lib/graphql/helpers';
import { useNotify } from '../../../hooks/useNotify';

//Takes an array of team roles and
// a) filters them against the given validator, and
// b) transforms them into a tree select input type
const filteredRoles = (
  roles: readonly StoredTeamRole[],
  validator: (role: StoredTeamRole) => boolean
) => {
  return roles.filter(validator).map((v) => {
    return {
      label: v.name,
      value: v.id,
      items: [],
    };
  });
};

/**
 * Function props for the Search User Select input component.
 */
type TeamRoleInputFunctionProps = {
  /**
   * Called when the selected value changes
   */
  readonly onChange?: (v: string) => void;
};

/**
 * Props for the Search User Select input component.
 */
type TeamRoleInputProps = TeamRoleInputFunctionProps & {
  /**
   * The current value of the input
   */
  readonly value?: string;
  /**
   * The name of current form
   */
  readonly formName?: string;
  /**
   * The name of current form list
   */
  readonly formListName?: string;
  /**
   * The field data of current form list
   */
  readonly field?: FormListFieldData;
  /**
   * Team ID for the roles to fetch
   */
  readonly teamId?: string;
  /**
   * Current logged in user's external user id.
   */
  readonly externalUserId: string;
};

/**
 * An input team role selector.
 * It is necessary that `onChange` and `value` are optional props (`?`) so that
 * this component can be used as an AntD form item.
 *
 * @returns an input box and role selection dropdown
 */
const TeamRoleInput = ({
  onChange,
  value: roleId,
  formName,
  formListName,
  field,
  teamId,
  externalUserId,
}: TeamRoleInputProps) => {
  const { notify } = useNotify();
  const [teamRoleOptions, setTeamRoleOptions] = useState<
    readonly StoredTeamRole[]
  >([]);

  const { data: teamRoleQueryResult, loading } = useQueryTeamRoles(
    onApolloError(notify.error),
    (data) =>
      setTeamRoleOptions(
        teamRolesByStatus(data.team_role, recordStatusSchema.Values.Active)
      ),
    true,
    teamId,
    externalUserId
  );

  const activeTeamRoles: readonly StoredTeamRole[] = teamRolesByStatus(
    teamRoleQueryResult?.team_role,
    recordStatusSchema.Values.Active
  );

  const onSearchTeamRoles = onTeamRoleSearch(
    activeTeamRoles,
    setTeamRoleOptions
  );

  const groupedRoles: readonly GroupWithOptions[] = roleGroupTypes.map(
    (group) => {
      return {
        groupTitle: group.toString(),
        options: filteredRoles(
          teamRoleOptions,
          (role) => role.role_group === group
        ).map((role) => ({ id: role.value, value: role.label })),
      };
    }
  );

  // Other roles are any roles that haven't been assigned a role group
  const otherRoles: GroupWithOptions = {
    groupTitle: 'Other',
    options: filteredRoles(
      teamRoleOptions,
      (role) =>
        !groupedRoles.some(
          (groupedRole) => groupedRole.groupTitle === role.role_group
        )
    ).map((role) => ({ id: role.value, value: role.label })),
  };

  const groupSelectId = generateAntdFormItemId([
    formName,
    formListName,
    field?.name,
    'teamRoleId',
  ]);

  // Since the antd form component value will not be updated automatically,
  // the value will be empty when we loading the team role and will ..
  // .. not be updated after loading is finished.
  // So we return blank block while loading to make sure the antd component ..
  // .. can only see and react on the latest team roles when query finished.
  if (loading) {
    return <></>;
  }

  return (
    <GroupSelect
      id={groupSelectId}
      className={'role-select'}
      onSearchTeamRoles={onSearchTeamRoles}
      onChange={onChange ?? (() => [])}
      value={{
        id: roleId ?? '',
        value:
          teamRoleQueryResult?.team_role.find((role) => role.id === roleId)
            ?.name ?? '',
      }}
      options={[
        ...groupedRoles.filter((group) => group.groupTitle !== 'SOC'),
        otherRoles,
      ]}
      showEmptyOption={false}
      title="Role Title"
    />
  );
};

export default TeamRoleInput;
